import { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  Typography,
} from '@material-ui/core';
import { Button } from '@shiftfinancial/design-system';
import queryString from 'query-string';
import { useFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import GetCapital from '../../svg/GetCapital';
import StixPointerIconSvg from '../../svg/StixPointerIconSvg';
import BusinessIconSvg from '../../svg/BusinessIconSvg';
import {
  GET_CAPITAL_PRIVACY_POLICY_URL,
  GTM_EVENT_TYPE_USER_JOURNEY,
  USER_JOURNEY_ACCOUNT_TYPE,
} from '../../../constants';
import Footer from '../../core/Footer';
import { gtmUserJourneyEvent } from '../../../utils/gtmUtils';

import actions from './actions';
import { formSchema } from './validations';

const useStyles = makeStyles((theme) => ({
  form: {
    backgroundColor: theme.palette.secondary.contrastText,
  },
  container: {
    [theme.breakpoints.down('xs')]: {
      backgroundColor: theme.palette.secondary.contrastText,
    },
    [theme.breakpoints.up('sm')]: {
      height: '100vh',
    },
  },
  mainContent: {
    backgroundColor: '#194C57',
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.up('sm')]: {
      height: 692,
      width: 930,
      padding: theme.spacing(12),
    },
  },
  logo: {
    display: 'block',
    marginBottom: `${theme.spacing(2)}px !important`,
    [theme.breakpoints.down('xs')]: {
      width: 100,
      maxWidth: 220,
      margin: 'auto',
    },
    [theme.breakpoints.up('sm')]: {
      width: 100,
    },
    [theme.breakpoints.up('xl')]: {},
  },
  stixIcon: {
    [theme.breakpoints.down('xs')]: {
      height: 89,
      width: 50,
    },
    [theme.breakpoints.up('sm')]: {
      height: 99,
      width: 60,
      marginBottom: theme.spacing(2),
    },
  },
  card: {
    backgroundColor: '#E7F6F9',
    borderRadius: '6px 6px 40px 6px',
    color: '#2E384D',
    boxShadow: `12px 12px ${theme.palette.secondary.contrastText}`,
    [theme.breakpoints.down('xs')]: {
      marginRight: theme.spacing(2),
      padding: theme.spacing(4),
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(4),
    },
  },
  info: {
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(14, 0, 0, 6),
    },
    '& h3': {
      margin: theme.spacing(3, 0, 0),
    },
    '& ul': {
      fontSize: 16,
      paddingLeft: theme.spacing(3),
      listStyle: 'none',
      fontWeight: 200,
      marginBottom: theme.spacing(4),
      '& li:before': {
        content: "'•'",
        color: '#00B6C0',
        fontWeight: 'bold',
        fontSize: 24,
        display: 'inline-block',
        width: '1em',
        marginLeft: '-1em',
        lineHeight: 1.3,
      },
      '& li': {
        marginBottom: '10px',
      },
    },
  },
  businessIcon: {
    [theme.breakpoints.down('xs')]: {
      height: 55,
      width: 55,
    },
    [theme.breakpoints.up('sm')]: {
      height: 75,
      width: 75,
    },
  },
  headerText: {
    lineHeight: '1.5 !important',
    fontSize: 22,
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.contrastText,
  },
  subContent: {
    borderLeft: '3px solid #8ADDE2',
    paddingLeft: theme.spacing(2),
  },
  checkboxFormControlLabel: {
    alignItems: 'flex-start',
  },
  checkboxLabel: {
    marginTop: '9px',
  },
  accountNameText: {
    color: '#106E78',
    fontWeight: 500,
  },
  contactNameText: {
    fontWeight: 500,
  },
  footer: {
    position: 'relative',
  },
}));

export function ReferralWelcome(props) {
  const location = useLocation();
  const dispatch = useDispatch();

  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    const gtmUserJourneyEventObject = {
      eventType: GTM_EVENT_TYPE_USER_JOURNEY.ACTIVATE_CLIENT,
      accountType: USER_JOURNEY_ACCOUNT_TYPE.BROKER_REFERRED_CLIENTS,
    };
    gtmUserJourneyEvent(gtmUserJourneyEventObject);
  }, []);

  useEffect(() => {
    const parsedSearch = queryString.parse(location.search);
    if (parsedSearch.referral_code) {
      dispatch(actions.getAccountContactRelationDetails(parsedSearch.referral_code)).then(
        (response) => {
          const { error, payload } = response;
          if (!error && payload?.data) {
            setReferralData(payload?.data[0]);
          }
        },
      );
    }
  }, [dispatch]);

  if (!referralData) {
    return null;
  }

  return <ReferralWelcomeForm referralData={referralData} />;
}

const BULLET_POINTS = [
  'We use the best technology to take away the paperwork.',
  'Solutions that work seamlessly with your existing bank.',
  'Access up to $500,000 without financial statements.',
];

function ReferralWelcomeForm({ referralData }) {
  const classes = useStyles();
  const history = useHistory();

  const { values, errors, submitCount, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      requireConfirmation: true,
      confirmation: false,
    },
    validationSchema: formSchema,
    onSubmit: () => {
      history.push('/registration');
    },
  });

  return (
    <div>
      <div>
        <form className={classes.form} noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Box
            className={classes.container}
            flexDirection="column"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              className={classes.mainContent}
              flexDirection="column"
              display="flex"
              alignItems="center"
              justifyContent="space-around"
              borderRadius={6}
            >
              <Grid container spacing={5}>
                <Grid item xs={12} sm={6}>
                  <GetCapital className={classes.logo} />
                  <Box className={classes.card} flexDirection="column" display="flex">
                    <StixPointerIconSvg className={classes.stixIcon} dark="true" />
                    <Typography variant="h2" className={classes.headerText}>
                      Welcome to Shift
                    </Typography>
                    <Box className={classes.subContent}>
                      <Typography variant="body2">
                        <div>
                          Your broker
                          <span className={classes.contactNameText}>
                            {` ${referralData?.firstName ?? ''} ${referralData?.lastName ?? ''} `}
                          </span>
                          from
                          <span
                            className={classes.accountNameText}
                          >{` ${referralData?.accountName ?? ''} `}</span>
                          has invited you to open an account.
                        </div>
                      </Typography>
                      <FormControlLabel
                        className={classes.checkboxFormControlLabel}
                        control={
                          <Checkbox
                            data-testid="uia-confirmation"
                            checked={values.confirmation}
                            onChange={(_, checked) => setFieldValue('confirmation', checked)}
                          />
                        }
                        label={
                          <Typography className={classes.checkboxLabel} variant="body2">
                            I agree to have my identity information verified with the issuer or
                            official record holder and I agree to Shift's{' '}
                            <Link
                              target="_blank"
                              rel="noopener noreferrer"
                              href={GET_CAPITAL_PRIVACY_POLICY_URL}
                            >
                              collection notice
                            </Link>
                            .
                          </Typography>
                        }
                      />
                      {errors.confirmation && submitCount > 0 && (
                        <FormHelperText variant="outlined" error>
                          {errors.confirmation}
                        </FormHelperText>
                      )}
                    </Box>

                    <Button
                      appearance="primary"
                      type="submit"
                      fullWidth
                      style={{ marginTop: '32px' }}
                      data-testid="uia-activateNowButton"
                    >
                      Activate now
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className={classes.info} flexDirection="column" display="flex">
                    <BusinessIconSvg className={classes.businessIcon} />
                    <Typography variant="h3">Set up will take just a few minutes.</Typography>
                    <ul>
                      {BULLET_POINTS.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </form>
      </div>
      <div className={classes.footer}>
        <Footer />
      </div>
    </div>
  );
}
